import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { hotkeyMatchesEvent, isTypingTarget } from '../hotkeys';
import { useHotkeySettings } from '../context/HotkeySettings';

/**
 * Registers the configured navigation hotkeys for the dashboard. Mounted once
 * from the Dashboard layout so the shortcuts work from any screen. No-op while
 * the feature is disabled.
 */
const useGlobalHotkeys = (): void => {
  const navigate = useNavigate();
  const {
    settings: { enabled, hotkeys },
  } = useHotkeySettings();

  useEffect(() => {
    if (!enabled) return;

    const handler = (event: KeyboardEvent): void => {
      // Require a modifier and never steal keystrokes while typing.
      if (!event.altKey && !event.ctrlKey && !event.shiftKey) return;
      if (isTypingTarget(event.target)) return;

      const hotkey = hotkeys.find((hk) => hotkeyMatchesEvent(event, hk));
      if (!hotkey) return;

      // Suppress browser accelerator behaviour before navigating.
      event.preventDefault();
      event.stopPropagation();
      navigate(hotkey.path);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate, enabled, hotkeys]);
};

export default useGlobalHotkeys;
